import React from 'react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import type { AIFeatureSetting, AIModelCatalog, AISettingsField, SettingsClient } from 'services/api/aiSettingsClient';
import AIFeatureEditor from './AIFeatureEditor';

const formatValue = (value: unknown, field?: AISettingsField): string => (typeof value === 'boolean'
  ? (value ? 'On' : 'Off')
  : field?.choice_labels?.[String(value)] ?? String(value));

/** One-line summary: the key model settings for AI features, every value otherwise. */
export const summarizeSetting = (feature: AIFeatureSetting): string => {
  const fieldFor = (key: string) => feature.fields.find((field) => field.key === key);
  const aiKeys = ['model', 'reasoning_effort', 'service_tier', 'provider', 'voice_id']
    .filter((key) => feature.effective[key] !== undefined && fieldFor(key));
  if (aiKeys.length) return aiKeys.map((key) => formatValue(feature.effective[key], fieldFor(key))).join(' · ');
  return feature.fields
    .filter((field) => field.kind !== 'messages' && field.kind !== 'text')
    .map((field) => (field.kind === 'boolean'
      ? `${field.label}: ${formatValue(feature.effective[field.key])}`
      : formatValue(feature.effective[field.key], field)))
    .join(' · ');
};

export const groupSettings = (features: AIFeatureSetting[]): Array<[string, AIFeatureSetting[]]> => {
  const byGroup = new Map<string, AIFeatureSetting[]>();
  features.forEach((feature) => byGroup.set(feature.group, [...(byGroup.get(feature.group) ?? []), feature]));
  return Array.from(byGroup.entries());
};

type FeatureRowProps = {
  feature: AIFeatureSetting;
  catalog?: AIModelCatalog;
  client?: SettingsClient;
  isOpen: boolean;
  onToggle: () => void;
  onSaved: (feature: AIFeatureSetting) => void;
};

export const FeatureRow: React.FC<FeatureRowProps> = ({ feature, catalog, client, isOpen, onToggle, onSaved }) => {
  const isCustom = Object.keys(feature.overrides).length > 0;
  const needsReview = Boolean(feature.stale_fields?.length);
  return (
    <li className="rounded-2xl border border-border bg-background px-5 py-4" data-testid={`ai-feature-${feature.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{feature.label}</h3>
            <Badge variant={isCustom ? 'default' : 'secondary'}>{isCustom ? 'Custom' : 'Default'}</Badge>
            {needsReview ? <Badge variant="destructive">Needs review</Badge> : null}
          </div>
          <p className="text-sm text-muted-foreground">{feature.description}</p>
          <p className="font-mono text-xs text-foreground" data-testid={`ai-feature-summary-${feature.key}`}>{summarizeSetting(feature)}</p>
          {feature.updated_at ? (
            <p className="text-xs text-muted-foreground">
              {`Changed ${new Date(feature.updated_at).toLocaleString()}${feature.updated_by ? ` by ${feature.updated_by}` : ''}`}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          size="auto"
          variant={isOpen ? 'default' : 'secondary'}
          aria-expanded={isOpen}
          aria-controls={`ai-feature-editor-${feature.key}`}
          onClick={onToggle}
        >
          {isOpen ? 'Editing' : 'Edit'}
        </Button>
      </div>
      {isOpen ? (
        <div id={`ai-feature-editor-${feature.key}`} className="mt-4">
          <AIFeatureEditor feature={feature} catalog={catalog} client={client} onSaved={onSaved} onClose={onToggle} />
        </div>
      ) : null}
    </li>
  );
};

type ManagedSettingsGroupsProps = {
  groups: Array<[string, AIFeatureSetting[]]>;
  catalog?: AIModelCatalog;
  client?: SettingsClient;
  openFeature: string | null;
  onToggle: (key: string) => void;
  onSaved: (feature: AIFeatureSetting) => void;
};

/** Grouped, editable settings rows shared by AI Settings and Operations app settings. */
export const ManagedSettingsGroups: React.FC<ManagedSettingsGroupsProps> = ({
  groups, catalog, client, openFeature, onToggle, onSaved,
}) => (
  <>
    {groups.map(([group, groupFeatures]) => (
      <div key={group} className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{group}</h2>
        <ul className="space-y-3">
          {groupFeatures.map((feature) => (
            <FeatureRow
              key={feature.key}
              feature={feature}
              catalog={catalog}
              client={client}
              isOpen={openFeature === feature.key}
              onToggle={() => onToggle(feature.key)}
              onSaved={onSaved}
            />
          ))}
        </ul>
      </div>
    ))}
  </>
);
